// Commissions → Overview: where the money stands, what needs Finance, and the
// settings that must be answered before live payouts.
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { BATCH_STATUS_LABEL, type Overview, errReason, money, periodLabel, fmtDay } from "@/lib/compPlans";
import { Button } from "@/components/ui/Button";
import type { GoToTab } from "./CommissionsPlansPage";
import { Notice, Pill, Section, Tiles, T_HEAD, T_NUM, T_ROW, T_TD, T_TH, T_WRAP } from "./parts";

export function OverviewTab({ go }: { go: GoToTab }) {
  const q = useQuery({ queryKey: ["commissions", "overview"], queryFn: () => api<Overview>("/commissions/overview") });
  if (q.isError) return <Notice tone="risk">{errReason(q.error)}</Notice>;
  const d = q.data;
  const t = d?.totals;
  const needsReview = Number(t?.needs_review ?? 0);

  return (
    <div>
      {d && d.missingSettings.length > 0 && (
        <div className="mb-8 rounded-md border-2 border-[#e2c58f] bg-warn-bg p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-warn" aria-hidden />
              <div>
                <h2 className="text-[19px] font-semibold text-warn">Finance must set before live payouts</h2>
                <p className="mt-1 text-[15px] text-warn">
                  {d.missingSettings.length} {d.missingSettings.length === 1 ? "question is" : "questions are"} still open. Nothing is
                  guessed — each one blocks only what it says.
                </p>
              </div>
            </div>
            <Button size="lg" onClick={() => go("settings")}>
              Answer in Settings
            </Button>
          </div>
          <ul className="mt-4 space-y-2 pl-9">
            {d.missingSettings.map((s) => (
              <li key={s.key} className="text-[15px] text-ink">
                <span className="mr-2 inline-block h-4 w-4 rounded border border-warn align-[-2px]" aria-hidden />
                {s.label}
                <span className="block pl-6 text-[14px] text-muted">Blocks: {s.blocks}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Section title="Where commission stands">
        <Tiles
          tiles={[
            { label: "Estimated", value: t ? money(t.estimated) : null, note: "Orders not eligible yet", onClick: () => go("orders", { status: "estimated" }) },
            { label: "Eligible", value: t ? money(t.eligible) : null, note: "Paid, closed, costs final — not yet approved", onClick: () => go("orders", { status: "eligible" }) },
            { label: "Approved for payout", value: t ? money(t.approved) : null, note: "In an approved batch, not paid yet", onClick: () => go("orders", { status: "approved_for_payout" }) },
            { label: "Paid this year", value: t ? money(t.paid_ytd) : null, onClick: () => go("orders", { status: "paid" }) },
            { label: "Approved bonuses (unpaid)", value: d ? money(d.approvedBonusesUnpaid) : null, onClick: () => go("bonuses") },
          ]}
        />
      </Section>

      <div className="grid gap-6 xl:grid-cols-2">
        <Section title="Needs Finance">
          <div className="divide-y divide-[#e7e7e4] rounded-md border border-rule bg-white">
            <NeedRow
              label="Orders needing review"
              hint="The plan that sets the pool must be chosen before these can become eligible."
              count={d ? needsReview : null}
              onClick={() => go("orders", { status: "needs_review" })}
            />
            <NeedRow
              label="Adjustments waiting for approval"
              count={d ? d.pendingAdjustments : null}
              onClick={() => go("adjustments")}
            />
            <NeedRow label="Bonuses waiting for approval" count={d ? d.pendingBonuses : null} onClick={() => go("bonuses")} />
          </div>
        </Section>

        <Section title="This payout period">
          <div className="rounded-md border border-rule bg-white p-5">
            {!d ? (
              <p className="text-[15px] text-muted">Loading…</p>
            ) : !d.calendar.anchor || !d.calendar.current ? (
              <p className="text-[15px] text-ink">
                The biweekly payout calendar isn't set yet.{" "}
                <button type="button" className="font-medium underline" onClick={() => go("settings")}>
                  Set it in Settings
                </button>
                .
              </p>
            ) : (
              <>
                <p className="text-[17px] font-medium text-ink">{periodLabel(d.calendar.current.start, d.calendar.current.end)}</p>
                {d.currentBatch ? (
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-[15px]">
                    <Pill tone={d.currentBatch.status === "paid" ? "active" : d.currentBatch.status === "approved" ? "warn" : "info"}>
                      {BATCH_STATUS_LABEL[d.currentBatch.status] ?? d.currentBatch.status}
                    </Pill>
                    <span className="tabular-nums">
                      {money(
                        Number(d.currentBatch.commission_total) + Number(d.currentBatch.bonus_total) + Number(d.currentBatch.adjustment_total),
                      )}
                    </span>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-medium underline"
                      onClick={() => go("payouts", { batch: d.currentBatch!.id })}
                    >
                      Open batch <ArrowRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                ) : (
                  <p className="mt-2 text-[15px] text-muted">
                    No batch for this period yet.{" "}
                    <button type="button" className="font-medium text-ink underline" onClick={() => go("payouts")}>
                      Create it in Payouts
                    </button>
                  </p>
                )}
              </>
            )}
          </div>
        </Section>
      </div>

      <Section
        title="Review for promotion"
        intro="Concierges whose sales are running at about $750k a year (last six months, annualized, backed by the full year). Promotion is a manual decision — change the plan in Plans & reps."
      >
        {!d ? null : d.promotionFlags.length === 0 ? (
          <p className="rounded-md border border-rule bg-white p-5 text-[15px] text-muted">No one is flagged right now.</p>
        ) : (
          <div className={T_WRAP}>
            <table className="w-full">
              <thead className={T_HEAD}>
                <tr>
                  <th className={T_TH}>Rep</th>
                  <th className={T_TH}>Concierge since</th>
                  <th className={`${T_TH} text-right`}>Last 6 months</th>
                  <th className={`${T_TH} text-right`}>Last 12 months</th>
                  <th className={`${T_TH} text-right`}>Annualized pace</th>
                  <th className={T_TH} />
                </tr>
              </thead>
              <tbody>
                {d.promotionFlags.map((f) => (
                  <tr key={f.repId} className={T_ROW}>
                    <td className={T_TD}>
                      <span className="font-medium">{f.repName}</span>{" "}
                      <Pill tone="warn">Review for promotion</Pill>
                    </td>
                    <td className={T_TD}>{fmtDay(f.onPlanSince)}</td>
                    <td className={T_NUM}>{money(f.trailing6)}</td>
                    <td className={T_NUM}>{money(f.trailing12)}</td>
                    <td className={T_NUM}>{money(f.annualized)}</td>
                    <td className={T_TD}>
                      <Button variant="outline" onClick={() => go("plans")}>
                        Plans & reps
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function NeedRow({ label, hint, count, onClick }: { label: string; hint?: string; count: number | null; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#fafaf8]">
      <span>
        <span className="block text-[16px] text-ink">{label}</span>
        {hint ? <span className="mt-0.5 block text-[14px] text-muted">{hint}</span> : null}
      </span>
      <span className={`text-[24px] font-medium tabular-nums ${count ? "text-warn" : "text-ink"}`}>{count ?? "…"}</span>
    </button>
  );
}
